import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import RegisterPage from './pages/RegisterPage';
import TrackingPage from './pages/TrackingPage';
import ResubmitPage from './pages/ResubmitPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientCreatePage from './pages/ClientCreatePage';
import ClientEditPage from './pages/ClientEditPage';
import ClientViewPage from './pages/ClientViewPage';
import UsersPage from './pages/UsersPage';
import ProfilePage from './pages/ProfilePage';
import PasswordResetPage from './pages/PasswordResetPage';
import SettingsPage from './pages/SettingsPage';
import PartnersPage from './pages/PartnersPage';
import FinancialDashboardPage from './pages/FinancialDashboardPage';
import ReferralRegistrationPage from './pages/ReferralRegistrationPage';
import PartnerTrackingPage from './pages/PartnerTrackingPage';
import WhatsAppPage from './pages/WhatsAppPage';
import DeletedClientsPage from './pages/DeletedClientsPage';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import { ThemeProvider } from './theme/ThemeProvider';
import { useAuth } from './hooks/useAuth';

function App() {
  const { user, loading } = useAuth();

  // Helper function to redirect based on user role
  const DashboardRedirect = () => {
    if (loading) {
      return <div>Loading...</div>;
    }
    
    if (!user) {
      return <Navigate to="/register" />;
    }
    
    // All authenticated users go to the same dashboard
    return <Navigate to="/dashboard" />;
  };

  return (
    <ThemeProvider>
      <Router>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<DashboardRedirect />} />
          <Route path="/register" element={
            <>
              <Navigation />
              <RegisterPage />
            </>
          } />
          <Route path="/tracking" element={
            <>
              <Navigation />
              <TrackingPage />
            </>
          } />
          <Route path="/resubmit/:trackingCode" element={
            <>
              <Navigation />
              <ResubmitPage />
            </>
          } />
          <Route path="/refer/:linkId" element={
            <>
              <ReferralRegistrationPage />
            </>
          } />
          <Route path="/partner-tracking" element={
            <>
              <Navigation />
              <PartnerTrackingPage />
            </>
          } />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          
          {/* Protected routes - Single dashboard for all authenticated users */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Navigation />
              <DashboardPage />
            </ProtectedRoute>
          } />
          
          {/* Protected routes - Client Management for both admin and employee */}
          <Route path="/clients" element={
            <ProtectedRoute>
              <Navigation />
              <ClientsPage />
            </ProtectedRoute>
          } />
          
          {/* Protected routes - Client Creation for both admin and employee */}
          <Route path="/create-client" element={
            <ProtectedRoute>
              <Navigation />
              <ClientCreatePage />
            </ProtectedRoute>
          } />
          
          {/* Protected routes - Client Edit for both admin and employee */}
          <Route path="/client/:id/edit" element={
            <ProtectedRoute>
              <Navigation />
              <ClientEditPage />
            </ProtectedRoute>
          } />
          
          {/* Protected routes - Admin only */}
          <Route path="/users" element={
            <ProtectedRoute requiredRole="admin">
              <Navigation />
              <UsersPage />
            </ProtectedRoute>
          } />
          
          {/* Protected routes - Both admin and employee */}
          <Route path="/client/:id" element={
            <ProtectedRoute>
              <Navigation />
              <ClientViewPage />
            </ProtectedRoute>
          } />
          <Route path="/profile" element={
            <ProtectedRoute>
              <Navigation />
              <ProfilePage />
            </ProtectedRoute>
          } />
          <Route path="/password-reset" element={
            <ProtectedRoute>
              <Navigation />
              <PasswordResetPage />
            </ProtectedRoute>
          } />
          <Route path="/settings" element={
            <ProtectedRoute>
              <Navigation />
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/partners" element={
            <ProtectedRoute>
              <Navigation />
              <PartnersPage />
            </ProtectedRoute>
          } />
          <Route path="/financial-dashboard" element={
            <ProtectedRoute>
              <Navigation />
              <FinancialDashboardPage />
            </ProtectedRoute>
          } />
          <Route path="/whatsapp" element={
            <ProtectedRoute requiredRole="admin">
              <Navigation />
              <WhatsAppPage />
            </ProtectedRoute>
          } />
          <Route path="/deleted-clients" element={
            <ProtectedRoute requiredRole="admin">
              <Navigation />
              <DeletedClientsPage />
            </ProtectedRoute>
          } />
          
          {/* Fallback route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
